import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { SettingsProvider } from './contexts/SettingsContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ProtectedRoute } from './components/admin/ProtectedRoute';
import { AuthCallback } from './components/admin/AuthCallback';
import { Header } from './components/Header';
import { HeroSection } from './components/HeroSection';
import { CountdownSection } from './components/CountdownSection';
import { WhySection } from './components/WhySection';
import { AboutSection } from './components/AboutSection';
import { DestinationsSection } from './components/DestinationsSection';
import { WomenEntrepreneursSection } from './components/WomenEntrepreneursSection';
import { SpeakersSection } from './components/SpeakersSection';
import { PackagesSection } from './components/PackagesSection';
import { GallerySection } from './components/GallerySection';
import { PartnersSection } from './components/PartnersSection';
import { SuccessStoriesSection } from './components/SuccessStoriesSection';
import { BrochureSection } from './components/BrochureSection';
import { ExhibitorSection } from './components/ExhibitorSection';
import { ContactSection } from './components/ContactSection';
import { Footer } from './components/Footer';
import { WhatsAppButton } from './components/WhatsAppButton';
import { AdminLogin } from './components/admin/AdminLogin';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { AdminEnquiries } from './components/admin/AdminEnquiries';
import { AdminMessages } from './components/admin/AdminMessages';
import { AdminSettings } from './components/admin/AdminSettingsFull';
import { VisualEditor } from './components/admin/VisualEditor';
import { LiveEditor } from './components/admin/LiveEditor';
import { AdminPartners } from './components/admin/AdminPartners';
import { AdminSuccessStories } from './components/admin/AdminSuccessStories';
import { AdminContacts } from './components/admin/AdminContacts';
import { AdminSpeakers } from './components/admin/AdminSpeakers';
import { AdminNotifications } from './components/admin/AdminNotifications';
import './App.css';

const LandingPage = () => {
  return (
    <div className="min-h-screen" id="top">
      <Header />
      <main className="pt-20">
        <HeroSection />
        <CountdownSection />
        <WhySection />
        <AboutSection />
        <DestinationsSection />
        <WomenEntrepreneursSection />
        <SpeakersSection />
        <PackagesSection />
        <GallerySection />
        <PartnersSection />
        <BrochureSection />
        <ExhibitorSection />
        <ContactSection />
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/en" element={<LandingPage />} />
      <Route path="/tr" element={<LandingPage />} />
      <Route path="/id" element={<LandingPage />} />
      
      {/* Admin login */}
      <Route path="/admin/login" element={<AdminLogin />} />
      
      {/* Protected admin routes */}
      <Route path="/admin" element={<ProtectedRoute />}>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="contacts" element={<AdminContacts />} />
        <Route path="live-editor" element={<LiveEditor />} />
        <Route path="editor" element={<VisualEditor />} />
        <Route path="partners" element={<AdminPartners />} />
        <Route path="speakers" element={<AdminSpeakers />} />
        <Route path="stories" element={<AdminSuccessStories />} />
        <Route path="enquiries" element={<AdminEnquiries />} />
        <Route path="messages" element={<AdminMessages />} />
        <Route path="notifications" element={<AdminNotifications />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <AdminAuthProvider>
      <SettingsProvider>
        <LanguageProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-right" />
          </BrowserRouter>
        </LanguageProvider>
      </SettingsProvider>
    </AdminAuthProvider>
  );
}

export default App;
